import { useRef, useState, useEffect, useCallback } from 'react'
import { Play, Pause, Volume2, VolumeX } from 'lucide-react'

interface AudioPlayerProps {
  src: string
  compact?: boolean
  className?: string
}

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2]

export default function AudioPlayer({ src, compact = false, className = '' }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const progressRef = useRef<HTMLDivElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isMuted, setIsMuted] = useState(false)
  const [speed, setSpeed] = useState(1)
  const [isDragging, setIsDragging] = useState(false)

  const formatTime = (time: number) => {
    if (!Number.isFinite(time) || time < 0) return '0:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const togglePlay = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    if (isPlaying) audio.pause()
    else audio.play()
  }, [isPlaying])

  const toggleMute = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.muted = !audio.muted
    setIsMuted(audio.muted)
  }, [])

  const cycleSpeed = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    const idx = SPEEDS.indexOf(speed)
    const next = SPEEDS[(idx + 1) % SPEEDS.length]
    audio.playbackRate = next
    setSpeed(next)
  }, [speed])

  const seek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current
    const bar = progressRef.current
    if (!audio || !bar || !Number.isFinite(audio.duration)) return
    const rect = bar.getBoundingClientRect()
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
    audio.currentTime = (x / rect.width) * audio.duration
  }, [])

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true)
    seek(e)
  }, [seek])

  useEffect(() => {
    if (!isDragging) return
    const handleMouseMove = (e: MouseEvent) => {
      const audio = audioRef.current
      const bar = progressRef.current
      if (!audio || !bar || !Number.isFinite(audio.duration)) return
      const rect = bar.getBoundingClientRect()
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
      audio.currentTime = (x / rect.width) * audio.duration
    }
    const handleMouseUp = () => setIsDragging(false)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    const onPlay = () => setIsPlaying(true)
    const onPause = () => setIsPlaying(false)
    const onTimeUpdate = () => setCurrentTime(audio.currentTime)
    const onLoadedMetadata = () => setDuration(audio.duration)
    const onEnded = () => { setIsPlaying(false); setCurrentTime(0) }
    audio.addEventListener('play', onPlay)
    audio.addEventListener('pause', onPause)
    audio.addEventListener('timeupdate', onTimeUpdate)
    audio.addEventListener('loadedmetadata', onLoadedMetadata)
    audio.addEventListener('ended', onEnded)
    return () => {
      audio.removeEventListener('play', onPlay)
      audio.removeEventListener('pause', onPause)
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.removeEventListener('loadedmetadata', onLoadedMetadata)
      audio.removeEventListener('ended', onEnded)
    }
  }, [src])

  useEffect(() => {
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
    setSpeed(1)
    if (audioRef.current) audioRef.current.playbackRate = 1
  }, [src])

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div
      role="region"
      aria-label="Reproductor de audio"
      className={`flex items-center gap-2 bg-gray-100 rounded-full px-3 ${compact ? 'h-8' : 'h-10'} select-none ${className}`}
      onContextMenu={(e) => e.preventDefault()}
    >
      <audio ref={audioRef} src={src} preload="metadata">
        <track kind="captions" srcLang="es" label="Español" />
      </audio>

      {/* Play/Pause */}
      <button
        type="button"
        onClick={togglePlay}
        className={`shrink-0 flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-colors ${compact ? 'w-6 h-6' : 'w-7 h-7'}`}
      >
        {isPlaying
          ? <Pause className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} fill="currentColor" />
          : <Play className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} ml-0.5`} fill="currentColor" />
        }
      </button>

      {/* Time */}
      <span className="text-xs text-gray-600 tabular-nums shrink-0 w-[38px] text-right">
        {formatTime(currentTime)}
      </span>

      {/* Progress bar */}
      <div
        ref={progressRef}
        role="slider"
        tabIndex={0}
        aria-label="Progreso del audio"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        className="flex-1 h-1.5 bg-gray-300 rounded-full cursor-pointer relative group"
        onMouseDown={handleMouseDown}
        onKeyDown={(e) => {
          const audio = audioRef.current
          if (!audio) return
          if (e.key === 'ArrowRight') audio.currentTime = Math.min(audio.currentTime + 5, audio.duration)
          if (e.key === 'ArrowLeft') audio.currentTime = Math.max(audio.currentTime - 5, 0)
        }}
      >
        <div
          className="h-full bg-blue-600 rounded-full relative transition-[width] duration-75"
          style={{ width: `${progress}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-blue-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow" />
        </div>
      </div>

      {/* Duration */}
      <span className="text-xs text-gray-500 tabular-nums shrink-0 w-[38px]">
        {formatTime(duration)}
      </span>

      {/* Speed control */}
      <button
        type="button"
        onClick={cycleSpeed}
        className={`shrink-0 text-xs font-bold tabular-nums rounded-md px-1.5 py-0.5 transition-colors ${speed !== 1 ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700 bg-gray-200 hover:bg-gray-300'}`}
      >
        {speed}x
      </button>

      {/* Mute toggle */}
      {!compact && (
        <button
          type="button"
          onClick={toggleMute}
          className="shrink-0 p-1 text-gray-500 hover:text-gray-700 transition-colors"
        >
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      )}
    </div>
  )
}
